import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Download, X, Share, Plus, Smartphone } from "lucide-react";

const DISMISS_KEY = "odf_pwa_banner_dismissed";

/**
 * Banner persistente "Instalar app" para el portal técnico.
 *  - Solo aparece en rutas /tecnico.
 *  - Android Chrome: usa beforeinstallprompt (instalación nativa).
 *  - iOS Safari: abre instrucciones para "Agregar a inicio".
 *  - Se oculta si ya está instalada (standalone) o si el técnico lo descartó.
 */
export default function InstallBanner() {
  const { pathname } = useLocation();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showIosHelp, setShowIosHelp] = useState(false);
  const [dismissed, setDismissed] = useState(
    typeof localStorage !== "undefined" && localStorage.getItem(DISMISS_KEY) === "1"
  );
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    const isStandalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (isStandalone) setInstalled(true);

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  const isIos = /iphone|ipad|ipod/i.test(ua);
  const isSafari = /^((?!chrome|android|crios|fxios).)*safari/i.test(ua);

  const onTecnico = pathname.startsWith("/tecnico");
  const canInstall = !!deferredPrompt || isIos;

  if (!onTecnico || installed || dismissed || !canInstall) return null;

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setDismissed(true);
  };

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      try {
        const choice = await deferredPrompt.userChoice;
        if (choice && choice.outcome === "accepted") dismiss();
      } catch (_) {}
      setDeferredPrompt(null);
      return;
    }
    setShowIosHelp(true);
  };

  return (
    <>
      <div
        className="fixed inset-x-0 bottom-0 z-40 bg-[#384091] text-white shadow-2xl pb-safe sm:inset-x-auto sm:right-4 sm:bottom-4 sm:rounded-xl sm:max-w-sm"
        role="region"
        aria-label="Instalar aplicación"
      >
        <div className="flex items-center gap-3 px-4 py-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white/15">
            <Smartphone className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold leading-tight">Instala Odontofix</p>
            <p className="text-[11px] leading-tight text-white/70">
              Accede más rápido desde tu pantalla de inicio.
            </p>
          </div>
          <Button
            type="button"
            size="sm"
            onClick={handleInstall}
            className="h-9 shrink-0 gap-1.5 bg-[#0FB4B7] text-white hover:bg-[#0D999C]"
          >
            <Download className="h-4 w-4" />
            Instalar
          </Button>
          <button
            type="button"
            onClick={dismiss}
            aria-label="Cerrar"
            className="shrink-0 rounded-md p-1 text-white/70 hover:bg-white/10 hover:text-white"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      <Dialog open={showIosHelp} onOpenChange={setShowIosHelp}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Smartphone className="h-5 w-5 text-[#0FB4B7]" />
              Instalar Odontofix
            </DialogTitle>
          </DialogHeader>
          {isIos && isSafari ? (
            <div className="space-y-3 text-sm text-gray-700">
              <p>Para instalar Odontofix en tu iPhone o iPad:</p>
              <ol className="space-y-2 list-decimal list-inside">
                <li className="flex items-center gap-2 flex-wrap">
                  <span>Toca el ícono</span>
                  <Share className="h-4 w-4 text-blue-600" />
                  <span>(Compartir) en la barra inferior.</span>
                </li>
                <li className="flex items-center gap-2 flex-wrap">
                  <span>Elige</span>
                  <Plus className="h-4 w-4" />
                  <span>"Agregar a pantalla de inicio".</span>
                </li>
                <li>Confirma con "Agregar".</li>
              </ol>
            </div>
          ) : (
            <p className="text-sm text-gray-700">
              Abre este sitio en <strong>Safari</strong> para poder instalar la app
              (otros navegadores en iOS no permiten instalar PWAs).
            </p>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
